import { ApolloClient, ApolloProvider, NormalizedCacheObject, gql } from '@apollo/client'
import cookie from 'js-cookie'
import { compose, get, getOr } from 'lodash/fp'
import MobileDetect from 'mobile-detect'
import App from 'next/app'
import Head from 'next/head'
import Router from 'next/router'
import { RefObject, createRef } from 'react'
import ReactModal from 'react-modal'
import { Provider } from 'react-redux'
import { Context as ResponsiveContext } from 'react-responsive'
import { v4 as uuidv4 } from 'uuid'
import { setLocale } from 'yup'

import { Breadcrumb } from '@goat/web-common'
import Banner, { ICMSBannerData } from 'banner/Banner'
import ExperimentProvider from 'experiments/providers/ExperimentProvider'
import FeatureFlagContextProvider from 'featureFlags/providers/FeatureFlagProvider'
import { HyperLinks, Metadata } from 'head'
import ImpactTag from 'head/components/ImpactTag'
import OneTrustTag from 'head/components/OneTrustTag'
import SnapchatPixel from 'head/components/SnapchatPixel'
import { getAbsoluteUrl, sanitizeUrl } from 'head/utils/seoHelpers'
import { LANGUAGE_COOKIE_NAME } from 'localization'
import { DEFAULT_LOCALE } from 'localization/enums/LocalizationEnums'
import { getReferrerInfo, getUtmQueryParams } from 'mParticle/mParticleService'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import 'normalize.css'
import { serverRequestService } from 'server/services/serverRequestService'
import { pruneUserFields } from 'server/utils/auth/pruneUserFields'
import withApolloClient from 'shared/apollo/withApolloClient'
import ShoppingRegionContextProvider from 'shared/contexts/ShoppingRegionContextProvider'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import withAuth from 'shared/hocs/withAuth'
import withBugsnagClient from 'shared/hocs/withBugsnagClient'
import withLocalization from 'shared/hocs/withLocalization'
import withTracking from 'shared/hocs/withTracking'
import PageLayout from 'shared/layouts/PageLayout'
import { sizes } from 'shared/lib/media'
import 'shared/polyfills.js'
import 'shared/styles/global.css'
import { User } from 'shared/types/User'
import { sortBreadcrumbChildren } from 'shared/utils/breadcrumb-utils'
import { setUser } from 'store/authSlice'
import { setupStore } from 'store/store'

interface AppProps {
  apolloClient: ApolloClient<NormalizedCacheObject>
  appContainerRef: RefObject<HTMLDivElement>
  footerData?: Breadcrumb
  isDesktop: boolean
  width: number
  canonicalUrl: string
  currentPath: string
  cmsBanner: ICMSBannerData
  featureFlags: any
  availableCountries: []
  availableCurrencies: []
  pathname: string
  user: User | null
  locale: string
  country: string
  currency: string
  currencyCode: string
  countryName: string
}

interface AppState {
  clientDedupID: string
  hasLoggedUtmEvent: false
  width: number
}

const GET_APP_DATA = gql`
  query getAppData {
    featureFlags
    availableCountries {
      isoCode
      name
      currency
      globalPricingShortCopy
      globalPricingBodyCopy
    }
    getCurrencies {
      isSelected @client
      isoCode
      symbol
    }
  }
`

const enhance = compose(
  withApolloClient,
  withTracking,
  withBugsnagClient,
  withAuth,
  withLocalization,
)

const store = setupStore({})

class FlightClubApp extends App<AppProps, AppState> {
  private appContainerRef: RefObject<HTMLDivElement>
  constructor(props) {
    super(props)
    this.state = {
      clientDedupID: uuidv4(),
      hasLoggedUtmEvent: false,
      width: null,
    }
    this.appContainerRef = createRef<HTMLDivElement>()

    this.setWidth = this.setWidth.bind(this)
  }

  // FIXME: Find a way to remove getInitialProps so we can enable Automatic Static Optimization
  // https://github.com/zeit/next.js/blob/canary/UPGRADING.md#check-your-custom--pages_appjs
  static async getInitialProps(context) {
    const { Component, ctx, apolloClient, router } = context

    let pageProps = {}
    let width = sizes.extraSmall

    const currentPath = getOr('', 'asPath', ctx)
    const originalUrl = ctx?.req?.originalUrl
    const { origin } = getAbsoluteUrl(ctx.req)
    const originLocation = `${origin}${originalUrl}`
    const sanitizedUrl = sanitizeUrl(originLocation)
    const localedRoute = router?.locale === 'ja-jp' || router?.locale === 'en-jp'
    const canonicalUrl = currentPath === '/' && !localedRoute ? `${sanitizedUrl}/` : sanitizedUrl
    const pathname = ctx?.pathname || ''
    const clientSideLocale = router?.locale || 'en'
    const country = ctx?.req?.cookies?.country || ctx.res?.locals?.country
    const currencyCode = ctx?.req?.cookies?.currencyCode || ctx.res?.locals?.currencyCode
    const countryName = ctx?.req?.cookies?.countryName || ctx.res?.locals?.countryName
    const currency = ctx?.req?.cookies?.currency || ctx.res?.locals?.currency
    const locale = ctx?.req?.cookies?.locale || ctx.res?.locals?.locale

    let featureFlags
    let availableCountries
    let availableCurrencies
    let user
    let footerData: Breadcrumb

    try {
      const { data } = await apolloClient.query({
        query: GET_APP_DATA,
      })

      featureFlags = data?.featureFlags || {}
      availableCountries = data?.availableCountries || {}
      availableCurrencies = data?.getCurrencies || {}
    } catch (e) {
      featureFlags = {}
      availableCountries = []
      availableCurrencies = []
    }

    const cmsBanner = !!ctx?.req?.cmsBanner && JSON.parse(ctx.req.cmsBanner)

    try {
      const { data } = await serverRequestService(ctx?.req, ctx?.res, 'sneakers', 'no-store').get(
        '/api/v1/users/me',
      )
      user = pruneUserFields(data)
    } catch (error) {
      user = null
    }

    try {
      const { data } = await serverRequestService(ctx?.req, ctx?.res, 'sneakers').get(
        '/api/v1/breadcrumbs/footer?includeChildren=true',
      )
      footerData = sortBreadcrumbChildren(data)
    } catch (e) {
      footerData = { children: [], name: '', slug: '' }
    }

    if (Component.getInitialProps) {
      pageProps = await Component.getInitialProps(ctx, apolloClient)
    }

    // Server Side
    if (typeof ctx.req !== 'undefined') {
      const md = new MobileDetect(ctx.req.headers['user-agent'])
      const isDesktop = !md.mobile()
      // Mobile automation sends a is_mobile cookie so that we can force mobile sizing when SSR
      const isMobileAutomation = get('cookies.is_mobile')(ctx.req)
      width = isDesktop && !isMobileAutomation ? sizes.extraLarge : sizes.extraSmall
    }
    // Client Side
    else if (typeof window !== 'undefined') {
      width = window.innerWidth
    }

    // Redirect to correct locale if needed
    if (ctx.res && featureFlags.tempWebFcLocalization) {
      if (
        country?.length &&
        locale?.length &&
        country === 'JP' &&
        locale !== DEFAULT_LOCALE &&
        ctx.locale.toLowerCase() !== locale.toLowerCase()
      ) {
        const path = ctx.asPath
        ctx.req.cookies.currency = JSON.stringify(
          availableCurrencies.find(
            (currentCurrency: any) => currentCurrency.isoCode === currencyCode,
          ),
        )
        ctx.req.cookies.currencyCode = currencyCode
        ctx.req.cookies.locale = locale
        ctx.res.writeHead(302, {
          Location: `/${locale}${path}`,
        })
        ctx.res.end()
      }
    }

    return {
      cmsBanner,
      pathname,
      featureFlags,
      footerData,
      pageProps,
      width,
      canonicalUrl,
      currentPath,
      user,
      availableCountries,
      availableCurrencies,
      country,
      currencyCode,
      countryName,
      currency,
      locale: clientSideLocale,
    }
  }

  setWidth() {
    this.setState({
      width: window.innerWidth,
    })
  }

  componentDidUpdate(): void {
    if (!this.state.hasLoggedUtmEvent) {
      const utmQueryParams = getUtmQueryParams()
      sendTrackingEvent('CAMPAIGN_SESSION_START', {
        ...getReferrerInfo(),
        ...utmQueryParams,
        client_dedup_id: this.state.clientDedupID,
      })
      this.setState({ ...this.state, hasLoggedUtmEvent: true })
    }
  }

  componentDidMount() {
    const { locale, user } = this.props
    setLocale({
      mixed: {
        required: '${path} is a required field.',
      },
    })

    if (user && !store.getState()?.auth?.isAuthenticated) {
      store.dispatch(setUser(user))
    }

    // HACKFINDER: https://github.com/zeit/next.js/issues/3065
    // known issue with back navigation with shallow routing
    Router.beforePopState(({ as }) => {
      window.location.href = as
      window.location.reload()
      return false
    })

    cookie.set(LANGUAGE_COOKIE_NAME, locale, { path: '/' })

    if (this.appContainerRef.current) {
      ReactModal.setAppElement(this.appContainerRef.current)
    }

    window.addEventListener('resize', this.setWidth)
    // FIXME: hack to fix the layout of cached html
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 0)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.setWidth)
  }

  render() {
    const {
      Component,
      apolloClient,
      footerData,
      cmsBanner,
      canonicalUrl,
      currentPath,
      featureFlags,
      pageProps,
      pathname,
      availableCountries,
      availableCurrencies,
      country,
      currencyCode,
      countryName,
      currency,
      locale,
    } = this.props
    const width = this.state.width || this.props.width
    // FIXME: temp logic for now
    const isCheckout = pathname === '/checkout' || pathname === '/checkoutSuccess'
    const isImpactTrackingEnabled = !!get(FeatureFlag.IMPACT_TRACKING)(featureFlags)
    const isOneTrustEnabled = !!get(FeatureFlag.ONE_TRUST)(featureFlags)
    const areHreflangEnabled = !!get(FeatureFlag.TEMP_WEB_FC_HREFLANG)(featureFlags)

    const baseUrl = 'https://www.flightclub.com'
    const enUSUrl = `${baseUrl}${currentPath}`
    const jaJPUrl = `${baseUrl}/ja-jp${currentPath !== '/' ? currentPath : ''}`
    const enJPUrl = `${baseUrl}/en-jp${currentPath !== '/' ? currentPath : ''}`

    return (
      <div ref={this.appContainerRef}>
        <Head>
          <title>Sneakers. Here. | Flight Club</title>
          {areHreflangEnabled && (
            <>
              <link rel="alternate" hrefLang="x-default" href={enUSUrl} />
              <link rel="alternate" hrefLang="en-us" href={enUSUrl} />
              <link rel="alternate" hrefLang="ja-jp" href={jaJPUrl} />
              <link rel="alternate" hrefLang="en-jp" href={enJPUrl} />
            </>
          )}
        </Head>
        {isOneTrustEnabled && <OneTrustTag />}
        <Metadata />
        {isImpactTrackingEnabled && <ImpactTag isOneTrustEnabled={isOneTrustEnabled} />}
        <ApolloProvider client={apolloClient}>
          <Provider store={store}>
            <SnapchatPixel isOneTrustEnabled={isOneTrustEnabled} />
            <ResponsiveContext.Provider value={{ width }}>
              <FeatureFlagContextProvider featureFlags={featureFlags}>
                <ShoppingRegionContextProvider
                  serverValues={{
                    country,
                    countryName,
                    currencyCode,
                    currency,
                  }}
                  availableCountries={availableCountries}
                  availableCurrencies={availableCurrencies}
                  clientSideLocale={locale}
                >
                  <ExperimentProvider>
                    <HyperLinks canonicalUrl={canonicalUrl} currentPath={currentPath} />
                    {!isCheckout && <Banner content={cmsBanner} />}
                    <PageLayout footerData={footerData}>
                      <Component {...pageProps} />
                    </PageLayout>
                  </ExperimentProvider>
                </ShoppingRegionContextProvider>
              </FeatureFlagContextProvider>
            </ResponsiveContext.Provider>
          </Provider>
        </ApolloProvider>
      </div>
    )
  }
}

export default enhance(FlightClubApp)
